import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { BrightLabsIcon } from '../Logo';
import { BRAND, whatsappHref } from '../../lib/brand';
import { LOCAL_AREAS } from '../../data/localAreas';

const SOCIALS = [
  { name: 'Facebook', icon: Facebook, url: BRAND.facebook },
  { name: 'Instagram', icon: Instagram, url: BRAND.instagram },
  { name: 'LinkedIn', icon: Linkedin, url: BRAND.linkedin },
];

const LOCAL_AREA_LINKS = LOCAL_AREAS.map((a) => ({ label: a.name, to: `/${a.slug}` }));

const COLUMNS = [
  {
    title: 'Industries',
    links: [
      { label: 'Trades', to: '/trades' },
      { label: 'Care Providers', to: '/care' },
      { label: 'Recruitment', to: '/recruitment' },
      { label: 'Gyms & Fitness', to: '/gyms' },
      { label: 'Clinics', to: '/clinics' },
      { label: 'Local Business', to: '/local-business' },
      { label: 'Professional Services', to: '/professional-services' },
    ],
  },
  {
    title: 'Areas We Serve',
    links: LOCAL_AREA_LINKS,
  },
  {
    title: 'Explore',
    links: [
      { label: 'What We Do', href: '/#what-we-do' },
      { label: 'Growth Score', href: '/#growth-score' },
      { label: 'Pricing', href: '/#pricing' },
      { label: 'FAQ', href: '/#faq' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Contact', to: '/contact' },
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'Terms of Service', to: '/terms' },
    ],
  },
];

// India-specific footer columns — no UK industries or Norfolk locations.
const INDIA_COLUMNS = [
  {
    title: 'For your industry',
    links: [
      { label: 'Coaching & Edtech', to: '/india#industries' },
      { label: 'Cafés & Restaurants', to: '/india#industries' },
      { label: 'Real Estate', to: '/india#industries' },
      { label: 'D2C & Ecommerce', to: '/india#industries' },
      { label: 'Salons & Beauty', to: '/india#industries' },
      { label: 'Wellness & Clinics', to: '/india#industries' },
      { label: 'Gyms & Fitness', to: '/india#industries' },
      { label: 'Wedding & Events', to: '/india#industries' },
    ],
  },
  {
    title: 'BrightLabs India',
    links: [
      { label: 'Pricing (in ₹)', to: '/india#pricing' },
      { label: 'Common Questions', to: '/india#faq' },
      { label: 'UK Site', to: '/' },
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'Terms of Service', to: '/terms' },
    ],
  },
];

export const AgencyFooter = () => {
  const { pathname } = useLocation();
  const isIndia = pathname.startsWith('/india');
  const columns = isIndia ? INDIA_COLUMNS : COLUMNS;
  // Balance India grid (only 2 link columns): brand=6, each col=3 → 12 units.
  // UK grid: brand=5, Industries=2, Areas=2, Explore=1, Company=1 → 11 units.
  const brandSpan = isIndia ? 'md:col-span-6' : 'md:col-span-5';
  const colSpan = (title) => {
    if (isIndia) return 'md:col-span-3';
    if (title === 'Industries' || title === 'Areas We Serve') return 'md:col-span-2';
    return 'md:col-span-1';
  };
  return (
  <footer className="bg-white py-16 border-t border-zinc-100" data-testid="agency-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand + Contact block */}
        <div className={brandSpan}>
          <div className="flex items-center gap-2.5 mb-4">
            <BrightLabsIcon className="h-10 w-10" />
            <span className="font-heading font-bold text-lg tracking-tight text-zinc-950">BrightLabs</span>
          </div>
          <p className="text-sm text-zinc-500 leading-relaxed max-w-sm mb-6 font-medium">
            {isIndia
              ? 'AI-powered growth systems for Indian small businesses. Websites. WhatsApp automation. SEO. Lead management. All-in-one — from ₹4,999/month.'
              : 'AI-powered growth systems for ambitious businesses. Websites. Automation. AI. Growth. Your outsourced growth department.'}
          </p>

          {/* Contact details — local trust */}
          <ul className="space-y-2.5 mb-6" data-testid="footer-contact-block">
            {isIndia ? (
              <li>
                <a
                  href={whatsappHref(BRAND.indiaWhatsApp, 'Hi BrightLabs, I saw your India site.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="group inline-flex items-center gap-2.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                >
                  <span className="w-7 h-7 rounded-lg bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <MessageCircle size={12} className="text-emerald-600" />
                  </span>
                  WhatsApp us
                </a>
              </li>
            ) : (
              <li>
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  data-testid="footer-phone-link"
                  className="group inline-flex items-center gap-2.5 text-sm font-semibold text-zinc-700 hover:text-zinc-950 transition-colors"
                >
                  <span className="w-7 h-7 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                    <Phone size={12} className="text-blue-600" />
                  </span>
                  {BRAND.phoneDisplay}
                </a>
              </li>
            )}
            <li>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email-link"
                className="group inline-flex items-center gap-2.5 text-sm font-semibold text-zinc-700 hover:text-zinc-950 transition-colors"
              >
                <span className="w-7 h-7 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                  <Mail size={12} className="text-blue-600" />
                </span>
                {BRAND.email}
              </a>
            </li>
            <li className="inline-flex items-center gap-2.5 text-sm font-medium text-zinc-500" data-testid="footer-location">
              <span className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                <MapPin size={12} className="text-blue-600" />
              </span>
              {isIndia ? BRAND.indiaLocationLine : BRAND.locationLine}
            </li>
            {!isIndia && (
              <li className="inline-flex items-center gap-2.5 text-sm font-medium text-zinc-500" data-testid="footer-hours">
                <span className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Clock size={12} className="text-blue-600" />
                </span>
                {BRAND.hoursDisplay}
              </li>
            )}
          </ul>

          <div className="flex gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.name}
                data-testid={`footer-social-${s.name.toLowerCase()}`}
                className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-400 hover:text-zinc-950 hover:border-zinc-400 transition-colors"
              >
                <s.icon size={15} />
              </a>
            ))}
          </div>
        </div>

        {columns.map((col) => (
          <div key={col.title} className={colSpan(col.title)}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-400 mb-4">{col.title}</p>
            <ul className="space-y-2.5">
              {col.links.map((l) => (
                <li key={l.label}>
                  {l.to ? (
                    <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, '-').replace(/[^\w-]/g, '')}`} className="text-sm font-medium text-zinc-500 hover:text-zinc-950 transition-colors">
                      {l.label}
                    </Link>
                  ) : (
                    <a href={l.href} className="text-sm font-medium text-zinc-500 hover:text-zinc-950 transition-colors">
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-14 pt-8 border-t border-zinc-100">
        <p className="text-xs text-zinc-400 font-medium">© {new Date().getFullYear()} BrightLabs · {isIndia ? BRAND.indiaLocationLine : BRAND.locationLine}</p>
        <p className="text-xs text-zinc-300 font-tech tracking-widest uppercase font-semibold">Execution Over Theory</p>
      </div>
    </div>
  </footer>
  );
};
